import { hypnogramIntervals } from "../components/hypnogram/ResultHypnogramPlot";
import { authDataIdentity, subscribeAuthDataIdentity } from "@rekonas/data-access";
import { useSyncExternalStore } from "react";
import { formatRecordingDate, recordingLabel } from "../recordingLabel";
import { RecordingMetricsSummary } from "./RecordingMetricsSummary";
import { getMetricsPositionReadiness, getMetricsPositionDistribution, metricsScope } from "@rekonas/data-access";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient, useIsFetching } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  authCacheScope,
  getCurrentHypnogram,
  hypnogramKeys,
  fetchOperationalCapabilities,
  fetchOperationalOverview,
  fetchOperationalWindow,
  getOperationalRecording,
  getOperationalReport,
  downloadOperationalReportPdf,
  operationalKeys,
} from "@rekonas/data-access";
import { SignalViewer, ViewerSettings, useViewerPreferences, profileWithOrientationModels, type ViewerDataSource, type ViewerStatusInfo } from "@rekonas/signal-viewer";
import { Alert, Button, Spinner } from "@rekonas/ui";
import { useOperationalWorkspace } from "../operationalWorkspace";
import { productStatus } from "./RecordingsPage";
import { OperationalBulkChannelPanel } from "./OperationalBulkChannelPanel";
import { OperationalHypnogramCard, SleepStagingIdentity } from "./OperationalHypnogram";
import { SummaryEmailAction } from "../components/SummaryEmails";

export function RecordingDetailPage() {
  const { workspaceRef = "", recordingRef = "" } = useParams();
  const accessIdentity=useSyncExternalStore(subscribeAuthDataIdentity,authDataIdentity,authDataIdentity);
  const viewPreferences = useViewerPreferences(authCacheScope());
  const [preferences] = viewPreferences;
  const workspaces = useOperationalWorkspace(workspaceRef);
  const query = useQuery({
    queryKey: operationalKeys.detail(workspaceRef, recordingRef),
    queryFn: ({ signal }) => getOperationalRecording(workspaceRef, recordingRef, signal),
    enabled: Boolean(workspaceRef && recordingRef),
    refetchInterval: (state) => state.state.data && !["ready", "attention_required", "failed"].includes(state.state.data.state) ? 5_000 : 10_000,
  });
  const client = useQueryClient();
  const metricsKey = [...metricsScope("", recordingRef), "current-summary"];
  const metricsFetching = useIsFetching({ queryKey: metricsKey }) > 0;
  const [metricsInformationTarget, setMetricsInformationTarget] = useState<HTMLDivElement | null>(null);
  const recording = query.data;
  const hypnogramQuery = useQuery({
    queryKey: hypnogramKeys.current(recordingRef, recording?.data_version || "", recording?.current_analysis?.run_ref || null),
    queryFn: async ({ signal }) => {
      const result = await getCurrentHypnogram(recordingRef, signal);
      if (result.state === "available" && (result.recording_ref !== recordingRef ||
          result.data_version !== recording?.data_version ||
          (result.analysis_run_ref || null) !== (recording?.current_analysis?.run_ref || null))) {
        throw new Error("Displayed sleep-staging result changed; refresh the recording.");
      }
      return result;
    },
    enabled: Boolean(recording?.data_version && recording.hypnogram === "available" &&
      recording.current_analysis?.compatible !== false), retry: false,
  });
  const [downloadError, setDownloadError] = useState("");
  const [viewerStatusInfo, setViewerStatusInfo] = useState<ViewerStatusInfo | null>(null);
  const reportQuery = useQuery({
    queryKey: ["operational-report", authCacheScope(), recordingRef],
    queryFn: ({ signal }) => getOperationalReport(recordingRef, signal),
    enabled: Boolean(recording),
    retry: false,
  });
  const report = reportQuery.data;
  const reportText = useMemo(() => report ? JSON.stringify(report.data, null, 2) ?? "null" : "", [report]);
  const reportPreview = reportText.length > 4_000 ? `${reportText.slice(0, 4_000)}\n… (truncated)` : reportText;

  async function downloadReport() {
    setDownloadError("");
    try {
      const blob = await downloadOperationalReportPdf(recordingRef);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `neoma-report-${recordingRef}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : "Report download failed.");
    }
  }
  const source = useMemo<ViewerDataSource | null>(() => {
    if (!recording?.profile || !recording.data_version || !recording.profile_sha256 || !recording.duration_seconds) return null;
    const pin = { workspaceRef, recordingRef: recording.recording_ref,
      dataVersion: recording.data_version, profileSha256: recording.profile_sha256 };
    return {
      key: [...operationalKeys.signalScope(workspaceRef, recording.recording_ref, recording.data_version), recording.current_analysis?.run_ref, recording.current_analysis?.compatible, accessIdentity],
      recordingRef: recording.recording_ref,
      durationSeconds: recording.duration_seconds,
      profile: profileWithOrientationModels(recording.profile),
      loadMetricsPositionReadiness: (signal) => getMetricsPositionReadiness(recording.recording_ref, signal),
      loadMetricsPositionDistribution: (identity, signal) => getMetricsPositionDistribution(recording.recording_ref, identity, signal),
      revalidateExport: async (signal) => {
        const current = await getOperationalRecording(workspaceRef, recording.recording_ref, signal);
        if (current.recording_ref !== recording.recording_ref || current.data_version !== recording.data_version ||
            current.profile_sha256 !== recording.profile_sha256 || current.duration_seconds !== recording.duration_seconds ||
            (current.current_analysis?.run_ref || null) !== (recording.current_analysis?.run_ref || null) ||
            current.current_analysis?.compatible !== recording.current_analysis?.compatible || current.hypnogram !== recording.hypnogram) {
          throw new Error("Displayed scientific result changed. Refresh the recording and try again.");
        }
      },
      loadExportOverview: async (modalities, maxPoints, signal) => {
        const overview = await fetchOperationalOverview({ ...pin, modalities, maxPoints, signal });
        // Mutable promoted stages are not pinned by the raw artifact's data_version.
        // Use the same frozen, already-authorized result as the standalone card,
        // rather than mixing a newly promoted overview overlay with that card.
        const result = hypnogramQuery.isError ? undefined : hypnogramQuery.data;
        const intervals = result?.state === "available" && recording.current_analysis?.compatible !== false
          ? hypnogramIntervals(result, recording.duration_seconds!) : [];
        return { ...overview, series: [
          ...overview.series.filter(s => s.modality_id !== "sleep_stage"),
          ...(modalities.includes("sleep_stage") && intervals.length ? [{
            modality_id: "sleep_stage", modality_label: "Sleep stage", channel_id: "sleep_stage.export",
            channel_label: "Sleep stage", origin: "derived" as const, unit: "category", representation: "intervals" as const,
            labels: Object.fromEntries(intervals.map(i => [i.stage, i.stage])),
            intervals: intervals.map(i => ({ start_seconds: i.start, end_seconds: i.end, code: i.stage })),
          }] : []),
        ] };
      },
      loadCapabilities: (signal) => fetchOperationalCapabilities({ ...pin, signal }),
      loadOverview: (modalities, maxPoints, signal) => fetchOperationalOverview({ ...pin, modalities, maxPoints, signal }),
      loadWindow: (modalities, startSeconds, durationSeconds, signal) => fetchOperationalWindow({
        ...pin, modalities, startSeconds, durationSeconds, signal,
      }),
    };
  }, [recording, workspaceRef, accessIdentity, hypnogramQuery.data, hypnogramQuery.isError]);

  useEffect(() => { document.title = recording ? `Night recording · Rekonas` : "Recording · Rekonas"; }, [recording]);

  if (workspaces.isPending || query.isPending) return <main className="operational-loading"><Spinner /> Loading recording…</main>;
  if (workspaces.isError || !workspaces.workspace || query.isError || !recording) return (
    <main className="operational-main"><Alert className="recording-list-alert">
      <h1>Recording could not be opened.</h1>
      <p>It may not exist in this authorized workspace.</p>
      <Link to="/recordings">Return to recordings</Link>
    </Alert></main>
  );

  const status = productStatus(recording);
  const date = formatRecordingDate(recording.display_date);
  const profile = recording.profile;
  const currentViewerStatus = viewerStatusInfo && viewerStatusInfo.sourceKey === source?.key.join("|") && viewerStatusInfo.recordingRef === recording.recording_ref &&
    viewerStatusInfo.dataVersion === recording.data_version ? viewerStatusInfo : null;
  return (
    <main className="operational-main operational-viewer-main">
      <Link className="operational-back" to="/recordings"><span aria-hidden="true">←</span> All recordings</Link>
      <section className="recording-detail-header">
        <div><p>Night recording</p><h1>{date}</h1><span>{recordingLabel(recording)}</span></div>
        <div className={`product-status status-${status.tone}`}>{status.label}</div>
        <div className="recording-header-actions">
          {workspaces.workspace.permissions.includes("recordings.export") && <SummaryEmailAction ids={[recording.recording_ref]} />}
          <details className="recording-info">
            <summary aria-label="About this recording" title="About this recording">i</summary>
            <div className="recording-info-panel">
              <h2>Recording and data identity</h2>
              <dl>
                <div><dt>Recording</dt><dd>{recording.recording_ref}</dd></div>
                <div><dt>Workspace</dt><dd>{recording.workspace_ref}</dd></div>
                <div><dt>Profile</dt><dd>{profile ? `${profile.profile_id} · ${profile.profile_version}` : "Pending"}</dd></div>
                <div><dt>Data version</dt><dd>{recording.data_version || "Pending"}</dd></div>
                <div><dt>Sleep-staging run</dt><dd>{recording.current_analysis?.run_ref || "Original processing result"}</dd></div>
                <div><dt>Signal profile SHA-256</dt><dd>{recording.profile_sha256 || "Pending"}</dd></div>
              </dl>
              <div ref={setMetricsInformationTarget} />
              <div className="recording-viewer-status-info">
                <p>{recording.device_summary.join(" · ") || "Device metadata pending"}{status.viewer && source
                  ? currentViewerStatus?.capabilitiesLoaded ? ` · ${currentViewerStatus.availableCount} available · ${currentViewerStatus.unavailableCount} unavailable` : " · Checking availability" : ""}</p>
                {status.viewer && source && currentViewerStatus && <>
                  <p>Selected time synchronizes signals, Sleep stage, head and torso</p>
                  <p>{currentViewerStatus.playbackActive ? "Buffered synchronized view" : "Manual synchronized view"} · native recording time</p>
                </>}
              </div>
            </div>
          </details>
          <Button className="refresh-button" size="sm" variant="ghost" onClick={() => void Promise.all([query.refetch(), client.invalidateQueries({ queryKey: metricsKey })])} disabled={query.isFetching || metricsFetching}><span className="refresh-icon-slot">{(query.isFetching || metricsFetching) && <Spinner />}</span><span>Refresh</span></Button>
        </div>
        {preferences.showRecordingMetrics && <RecordingMetricsSummary informationTarget={metricsInformationTarget} key={`${authCacheScope()}:${recordingRef}`} recordingRef={recordingRef} dataVersion={recording.data_version || ""} stageRun={hypnogramQuery.data?.analysis_run_ref || recording.current_analysis?.run_ref || null} />}
      </section>
      <div className="recording-detail-refresh-status" aria-live="polite">{query.isError && <Alert>Refresh failed. The last authorized recording remains visible.</Alert>}</div>
      {preferences.showSleepStagingInformation && <SleepStagingIdentity recording={recording} result={hypnogramQuery.data}
        loading={hypnogramQuery.isPending && hypnogramQuery.isFetching && recording.hypnogram === "available"}
        error={hypnogramQuery.isError} canManage={Boolean(workspaces.workspace.permissions?.includes("analyses.manage"))} />}
      {report && <section className="operational-report-bridge" aria-label="Imported report">
        <Button type="button" onClick={() => void downloadReport()}>Download report</Button>
        {downloadError && <Alert role="alert">{downloadError}</Alert>}
        <h2>Report JSON · temporary preview</h2>
        <pre>{reportPreview}</pre>
      </section>}
      {preferences.showBulkChannelHandoff && source && recording.signals === "available" && <OperationalBulkChannelPanel key={JSON.stringify(source.key)} source={source} />}
      {!status.viewer || !source ? (
        <section className="processing-recording-state">
          <div className="viewer-controls"><ViewerSettings key={authCacheScope()} preferences={preferences} setPreferences={viewPreferences[1]} pageCards profile={source?.profile || { version: "1.1", profile_id: "pending", profile_version: "1", time_basis: "seconds_from_recording_start", modalities: [] }} /></div>
          <p>{recording.signals === "available" && recording.viewer === "failed" ? "Viewer preparation" : "Signal processing"}</p><h2>{recording.signals === "failed" ? "Signals unavailable" : recording.viewer === "failed" ? "Viewer preparation failed" : "This recording is still being prepared"}</h2>
          <span>{recording.signals === "failed"
            ? "Signal processing failed. No scientific data is presented as ready."
            : recording.viewer === "failed"
              ? "Viewer data could not be prepared. The recording remains available for an operator to retry preparation."
            : "The recording remains visible and this page will update while processing continues."}</span>
        </section>
      ) : (
        <SignalViewer
          attention={recording.hypnogram === "failed" ? "Signals are available. Sleep analysis failed, so Sleep stage remains unavailable for this version." :
            recording.current_analysis && !recording.current_analysis.compatible ? "The promoted sleep-staging result belongs to a different scientific data version. Its stages are withheld for this recording version." :
            recording.hypnogram === "unsupported_mapping" ? "Signals are available. Sleep stage is unavailable because compatible EEG was not recorded." : null}
          viewPreferences={viewPreferences}
          pageCards
          dataSource={source}
          preferenceIdentity={authCacheScope()}
          recordingLabel={`${recordingLabel(recording)} · ${date}`}
          qualityLabel={recording.device_summary.join(" · ") || "Device metadata pending"}
          onStatusInfoChange={setViewerStatusInfo}
          renderHypnogram={(viewer) => <OperationalHypnogramCard recording={recording}
            result={hypnogramQuery.data} loading={hypnogramQuery.isPending && hypnogramQuery.isFetching}
            error={hypnogramQuery.isError} viewer={viewer} />}
        />
      )}
    </main>
  );
}
